export type SourceModule = 'ad' | 'sm';

export interface SourceModuleConfig {
  key: SourceModule;
  label: string;
  description: string;
  tablePrefix: string;
  icon: string;
  color: string;
}

export const SOURCE_MODULES: Record<SourceModule, SourceModuleConfig> = {
  ad: {
    key: 'ad',
    label: 'Art Director',
    description: 'Brand identity, logos, colors, visual assets',
    tablePrefix: 'ad_',
    icon: 'Palette',
    color: 'text-pink-400',
  },
  sm: {
    key: 'sm',
    label: 'School Management',
    description: 'School metadata, contacts, locations, custom attributes',
    tablePrefix: 'sm_',
    icon: 'GraduationCap',
    color: 'text-blue-400',
  },
};

export interface DiscoveredColumn {
  column_name: string;
  data_type: string;
  udt_name: string;
  is_nullable: boolean;
  column_default: string | null;
  ordinal_position: number;
  character_maximum_length: number | null;
}

export interface DiscoveredVirtualField {
  field_key: string;
  label: string;
  category: string;
  data_type: string;
}

export interface DiscoveredTable {
  table_name: string;
  table_schema: string;
  source: SourceModule;
  columns: DiscoveredColumn[];
  row_count_estimate: number | null;
  description: string | null;
  virtual_columns?: DiscoveredVirtualField[];
}

export interface SchemaDiscoveryResult {
  tables: DiscoveredTable[];
  discoveredAt: string;
  error: string | null;
}

export type MappableProperty =
  | 'textContent'
  | 'fontFamily'
  | 'fontSize'
  | 'fontColor'
  | 'fontWeight'
  | 'textTransform'
  | 'imageUrl'
  | 'imageSrc'
  | 'fillColor'
  | 'strokeColor'
  | 'buttonLabel'
  | 'buttonHref'
  | 'buttonFillColor'
  | 'opacity'
  | 'visible';

export interface MappablePropertyConfig {
  key: MappableProperty;
  label: string;
  description: string;
  supportedElementTypes: Array<'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line'>;
  compatibleDataTypes: string[];
  category: 'content' | 'typography' | 'color' | 'media' | 'interaction' | 'display';
}

export const MAPPABLE_PROPERTIES: MappablePropertyConfig[] = [
  {
    key: 'textContent',
    label: 'Text Content',
    description: 'The text displayed in the element',
    supportedElementTypes: ['text', 'button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying', 'name', 'char'],
    category: 'content',
  },
  {
    key: 'buttonLabel',
    label: 'Button Label',
    description: 'The label text on a button element',
    supportedElementTypes: ['button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying', 'name'],
    category: 'content',
  },
  {
    key: 'buttonHref',
    label: 'Button Link URL',
    description: 'The URL the button links to',
    supportedElementTypes: ['button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'interaction',
  },
  {
    key: 'fontFamily',
    label: 'Font Family',
    description: 'The font used for text rendering',
    supportedElementTypes: ['text', 'button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'typography',
  },
  {
    key: 'fontSize',
    label: 'Font Size',
    description: 'The size of the text in pixels',
    supportedElementTypes: ['text', 'button'],
    compatibleDataTypes: ['integer', 'smallint', 'numeric', 'real', 'double precision', 'int4', 'int2', 'float4', 'float8'],
    category: 'typography',
  },
  {
    key: 'fontWeight',
    label: 'Font Weight',
    description: 'The weight/boldness of the text',
    supportedElementTypes: ['text', 'button'],
    compatibleDataTypes: ['integer', 'smallint', 'numeric', 'text', 'varchar', 'int4', 'int2'],
    category: 'typography',
  },
  {
    key: 'textTransform',
    label: 'Text Transform',
    description: 'Text capitalization (uppercase, lowercase, capitalize)',
    supportedElementTypes: ['text', 'button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'typography',
  },
  {
    key: 'fontColor',
    label: 'Text Color',
    description: 'The color of the text',
    supportedElementTypes: ['text', 'button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'color',
  },
  {
    key: 'fillColor',
    label: 'Fill Color',
    description: 'The fill color of a shape, path, or SVG element',
    supportedElementTypes: ['shape', 'path', 'svg', 'line'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'color',
  },
  {
    key: 'buttonFillColor',
    label: 'Button Fill Color',
    description: 'The background fill color of a button element',
    supportedElementTypes: ['button'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'color',
  },
  {
    key: 'strokeColor',
    label: 'Stroke Color',
    description: 'The border/stroke color',
    supportedElementTypes: ['shape', 'path', 'svg', 'line'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'color',
  },
  {
    key: 'imageUrl',
    label: 'Image URL',
    description: 'The URL of the image to display',
    supportedElementTypes: ['image'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'media',
  },
  {
    key: 'imageSrc',
    label: 'Image Source',
    description: 'The source path or URL of the image',
    supportedElementTypes: ['image'],
    compatibleDataTypes: ['text', 'varchar', 'character varying'],
    category: 'media',
  },
  {
    key: 'opacity',
    label: 'Opacity',
    description: 'The transparency level (0-1)',
    supportedElementTypes: ['text', 'image', 'shape', 'button', 'path', 'svg', 'line'],
    compatibleDataTypes: ['numeric', 'real', 'double precision', 'float4', 'float8'],
    category: 'display',
  },
  {
    key: 'visible',
    label: 'Visibility',
    description: 'Whether the element is visible',
    supportedElementTypes: ['text', 'image', 'shape', 'button', 'path', 'svg', 'line'],
    compatibleDataTypes: ['boolean', 'bool'],
    category: 'display',
  },
];

export interface EnhancedFieldMapping {
  id: string;
  source: SourceModule;
  tableName: string;
  columnName: string;
  columnDataType: string;
  targetProperty: MappableProperty;
  fallbackValue: string;
  transformExpression: string | null;
  isValid: boolean;
  validationErrors: string[];
  createdAt: string;
  filterColumn?: string | null;
  filterValue?: string | null;
  filterCategory?: string | null;
}

export interface MappingValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface PreviewRecord {
  [columnName: string]: unknown;
}

export interface PreviewState {
  source: SourceModule;
  tableName: string;
  records: PreviewRecord[];
  selectedRecordIndex: number;
  loading: boolean;
  error: string | null;
  fetchedAt: string | null;
}

export interface MappingStoreState {
  schema: SchemaDiscoveryResult | null;
  schemaLoading: boolean;
  schemaError: string | null;
  lastSchemaRefresh: string | null;
  elementMappings: Record<string, EnhancedFieldMapping[]>;
  preview: PreviewState | null;
  selectedSource: SourceModule | null;
  selectedTable: string | null;
  selectedColumn: string | null;
  mappingPanelOpen: boolean;
}
